import { css, html, LitElement, nothing } from 'lit';
import { v4 as uuid } from 'uuid';

import { classMap } from 'lit/directives/class-map.js';
import { bodySmallStyles, heading3Styles } from '@brightspace-ui/core/components/typography/styles';
import { RequesterMixin } from '@brightspace-ui/core/mixins/provider-mixin';

import '@brightspace-ui/core/components/expand-collapse/expand-collapse-content';
import '@brightspace-ui/core/components/icons/icon';
import '@brightspace-ui/core/components/button/button';
import '@brightspace-ui/core/components/button/button-icon.js';
import '@brightspace-ui/core/components/button/button-subtle.js';
import '@brightspace-ui/core/components/form/form.js';
import '@brightspace-ui/core/components/dialog/dialog';
import '@brightspace-ui/core/components/dialog/dialog-confirm';
import '@brightspace-ui/core/components/inputs/input-text';
import '@brightspace-ui/core/components/switch/switch.js';
import '@brightspace-ui/core/components/tooltip/tooltip.js';
import '@brightspace-ui/core/components/status-indicator/status-indicator';
import '@brightspace-ui/core/components/loading-spinner/loading-spinner.js';

import { inputLabelStyles } from '@brightspace-ui/core/components/inputs/input-label-styles';
import { selectStyles } from '@brightspace-ui/core/components/inputs/input-select-styles.js';

import { cardStyles } from '../../styles/card.js';

import { getDisplayName } from '../../helpers/helpers.js';

class FussUserList extends RequesterMixin(LitElement) {

  static get properties() {
    return {
      readonly: { type: Boolean, attribute: 'readonly' },
      relayState: { type: String, attribute: 'relay-state' },
      tenantId: { type: String, attribute: 'tenant-id' },
      domain: { type: String, attribute: 'domain' },
      environmentId: { type: String, attribute: 'environment-id' },
      environment: { type: Object, reflect: false },
      loginType: { type: String, attribute: 'login-type' },
      allowableDomains: { type: Array },
      bypassLoginLinkTenants: { type: Array },
      users: { type: Array },
      tenant: { type: Object },
      _filteredUsers: { type: Array },
      _loginLink: { type: String },
      _userToRemove: { type: Object },
      _krakenUsers: { type: Array },
      _filteredKrakenUsers: { type: Array },
      _tenantRoles: { type: Array },
      _userRoles: { type: Object },
    };
  }

  static get styles() {
    return [
      bodySmallStyles,
      heading3Styles,
      inputLabelStyles,
      selectStyles,
      cardStyles,
      css`
        :host {
          display: block;
        }
        .user-content {
          height: 100%;
          align-self: center;
          display: grid;
          place-items: center;
        }
        .expanded-content {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1rem;
          padding: 24px 0 0 0;
        }
        .show-more-button {
          padding-top: 24px;
          margin: 0 auto;
          display: block;
          text-align: center;
        }
        .save-button {
          text-align: end;
        }
        .logo:hover {
          cursor: pointer;
          transform: scale(1.25);
          transition: transform 0.3s ease-in-out;
        }
        .bold {
          font-weight: bold;
        }
        .alert {
          color: var(--d2l-color-cinnabar);
        }
        .user-alert {
          color: var(--d2l-color-cinnabar);
          margin: revert;
        }
        .status-indicator {
          margin: auto 0;
        }
        .status-indicator-admin {
          color: blue;
          border-color: blue;
        }
        .status-indicator-watcher {
          color: black;
          border-color: black;
        }
        .roles-section {
          padding-top: 12px;
          border-top: 1px solid var(--d2l-color-mica);
          margin-top: 12px;
        }
        .roles-header {
          font-weight: bold;
          margin-bottom: 8px;
        }
        .role-chips {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
          margin-bottom: 8px;
        }
        .role-chip {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          background: var(--d2l-color-celestine-plus-2);
          border: 1px solid var(--d2l-color-celestine);
          border-radius: 12px;
          padding: 2px 8px;
          font-size: 0.8rem;
        }
        .role-assign {
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .no-roles-warning {
          color: var(--d2l-color-cinnabar);
          font-size: 0.85rem;
        }
      `];
  }

  constructor() {
    super();
    this.readonly = false;
    this._filteredUsers = [];
    this.relayState = '/';
    this._userToRemove = {};
    this._krakenUsers = [];
    this._filteredKrakenUsers = [];
    this._tenantRoles = [];
    this._userRoles = {};
  }

  connectedCallback() {
    super.connectedCallback();
    this.client = this.requestInstance('d2l-pm-client');
  }

  get removeUserDialog() {
    if (!this._userDialog) this._userDialog = this.shadowRoot.getElementById('remove-user-dialog');
    return this._userDialog;
  }

  _openRemoveUserDialog(user) {
    return e => {
      e.stopPropagation();
      this._userToRemove = { ...user };

      // Ensure the dialog opens after rendering updates
      this.updateComplete.then(() => {
        this.removeUserDialog.opened = true;
      });
    };
  }

  async firstUpdated() {

    this.users.sort((a, b) => {
      const aval = getDisplayName(a);
      const bval = getDisplayName(b);
      return aval.localeCompare(bval);
    });
    this._filteredUsers = [...this.users];
    this.environment = await this.client.fetchEnvironment(this.environmentId);

    try {
      this._tenantRoles = await this.client.fetchTenantRoles(this.environmentId, this.tenantId) || [];
    } catch {
      this._tenantRoles = [];
    }
  }

  userAttributeUpdated(user) {
    return async e => {
      const key = e.target.id;
      user[key] = e.target.value;
      this.update();
    };
  }

  updateFussUser(user) {
    return async() => {
      const form = this.shadowRoot.getElementById(`user-form-${user.guid}`);
      const invalid = await form.validate();
      if (invalid.size === 0) {
        // Push the updated user data to the backend
        await this.client.pushToFuss(this.environmentId, this.tenantId, user);

        // Update the user in the list
        const index = this._filteredUsers.findIndex(u => u.guid === user.guid);
        if (index !== -1) {
          // Update the user data and collapse all expanded attributes
          this._filteredUsers[index] = { ...user, expanded: false };
          this._filteredUsers = [...this._filteredUsers]; // Ensure reactivity
        }
      }
    };
  }

  activeStateChange(user) {
    return async e => {
      user.active = e.target.on;
    };
  }

  brazeClicked(guid) {
    return e => {
      e.stopPropagation();
      window.open(`https://dashboard-07.braze.com/users/user_search/65e23e02e3acf1004aa0ce9b?locale=en&query=${guid}`, '_blank');
    };
  }

  waveClicked(guid) {
    return e => {
      e.stopPropagation();
      this.dispatchEvent(new CustomEvent('wave-user-clicked', { detail: { guid } }));
    };
  }

  amplitudeClicked(guid) {
    return e => {
      e.stopPropagation();
      window.open(`https://app.amplitude.com/analytics/d2l/project/${this.environment?.amplitudeProjectId}/search/user_id%3D${guid}`, '_blank');
    };
  }

  async _removeUser() {
    const ret = await this.client.removeFussUser(this.environmentId, this.tenantId, this._userToRemove);
    if (ret) {
      const index = this._filteredUsers.findIndex(user => user.guid === this._userToRemove.guid);
      this._filteredUsers.splice(index, 1);
      this._filteredUsers = [...this._filteredUsers];
    }

  }

  alertTemplate(user) {
    const id = `alert_${user.guid}`;
    if (this.loginType === 'magic-link') {
      let tooltipText;
      const email = user?.thirdPartyUserId?.indexOf('@') > 0 ? user.thirdPartyUserId : user.email;
      if (this.domain && !this.bypassLoginLinkTenants.includes(this.domain) && !this.bypassLoginLinkTenants.includes('all')) tooltipText = 'This domain wont allow login through Kraken';
      else if (!user.active) tooltipText = 'User is inactive';
      else if (!email) tooltipText = 'User missing email address';
      else if (!this.allowableDomains.includes(email?.split('@')[1]) && !this.allowableDomains.includes(email)) tooltipText = 'User email not compatible with allowable domains';

      if (tooltipText) {
        return html`<div class="d2l-body-small user-alert"><d2l-icon class="alert" id="${id}" icon="tier1:alert"></d2l-icon> <span>${tooltipText}</span></div>`;
      }
    }
    return nothing;
  }

  _userListItemTemplate(user) {
    const toggleExpandedUserAttributes = e => {
      e.stopPropagation();
      const button = e.target;
      const expandedContent = this.shadowRoot.querySelector(`#expanded-attributes_${user.guid}`);
      button.text = expandedContent.expanded ? 'Show more' : 'Show less';
      expandedContent.expanded = !expandedContent.expanded;
    };

    const entitlementClasses = entitlement => {
      return {
        'status-indicator-admin': entitlement === 'admin',
        'status-indicator-sponsor': entitlement === 'sponsor',
        'status-indicator-watcher': entitlement === 'watcher',
        'status-indicator': true,
      };
    };

    const getEntitlmentsForUser = this.tenant.entitlements.filter(entitlement => entitlement.id === user.thirdPartyUserId);
    return html`
      <d2l-list-item
        ?expandable="${!this.readonly}"
        ?expanded="${user.expanded}"
        grid
        @click="${this._userClicked(user)}"
        href="javascript:void(0)"
        key="${user.guid}"
        label="${getDisplayName(user)}">
        <profile-image .user="${user}" meta-attribute="${this.readonly ? '' : 'guid'}"></profile-image>
        ${getEntitlmentsForUser.map(e => html`
          <d2l-status-indicator
            id="${e.id}"
            class="${classMap(entitlementClasses(e.entitlement))}"
            text="${e.entitlement}">
          </d2l-status-indicator>
          <d2l-tooltip for="${e.id}" position="bottom">${getDisplayName(user)} has the '${e.entitlement} entitlement</d2l-tooltip>
        `)}
        ${this.alertTemplate(user)}
        ${this.readonly ? nothing : html`
        <div slot="actions">
          <d2l-button-icon text="Copy login url" @click="${this._copyUrl(user)}" icon="html-editor:table-row-copy"></d2l-button-icon>
          <d2l-button-icon text="Copy guid" @click="${this._copyGuid(user)}" icon="html-editor:cut"></d2l-button-icon>
          <d2l-button-icon text="Open in new window" @click="${this._userClicked(user, true)}" icon="tier1:new-window"></d2l-button-icon>
          <d2l-button-icon text="Remove user" @click="${this._openRemoveUserDialog(user)}"  icon="tier1:close-default"></d2l-button-icon>
        </div>
        `}
        ${this.readonly ? nothing : html`
        <d2l-list @click="${this._stopProp}" slot="nested">
          <d2l-form id="user-form-${user.guid}" @change="${this.userAttributeUpdated(user)}">
            <d2l-switch ?on="${user.active}" @change="${this.activeStateChange(user)}" text="Active"></d2l-switch>
            <img class="logo" @click="${this.brazeClicked(user.guid)}" src="assets/img/braze.png" height="35" alt="">
            ${this.environment?.amplitudeProjectId ? html`<img class="logo" @click="${this.amplitudeClicked(user.guid)}" src="assets/img/amplitude.png" height="35" alt="">` : nothing}
            <img class="logo" @click="${this.waveClicked(user.guid)}" src="assets/img/skillswave-a.svg" height="35" alt="">
            <div class="expanded-content">
              <d2l-input-text id="thirdPartyUserId" value="${user.thirdPartyUserId}" label="Third party user id" required></d2l-input-text>
              <d2l-input-text id="email" value="${user.email}" label="Email" required></d2l-input-text>
              <d2l-input-text id="firstName" value="${user.firstName}" label="First name" required></d2l-input-text>
              <d2l-input-text id="lastName" value="${user.lastName}" label="Last name" required></d2l-input-text>
              <d2l-input-text id="supervisorDisplayName" value="${user.supervisorDisplayName}" label="Manager name"></d2l-input-text>
              <d2l-input-text id="supervisorThirdPartyUserId" value="${user.supervisorThirdPartyUserId}" label="Manager ID"></d2l-input-text>
            </div>
            <d2l-expand-collapse-content id="expanded-attributes_${user.guid}">
              <div class="expanded-content">
                <d2l-input-text id="preferredLanguage" value="${user.preferredLanguage}" label="Preferred language"></d2l-input-text>
                <d2l-input-text id="preferredName" value="${user.preferredName}" label="Preferred name"></d2l-input-text>
                <d2l-input-text id="jobTitle" value="${user.jobTitle}" label="Job title"></d2l-input-text>
                <d2l-input-text id="department" value="${user.department}" label="Department"></d2l-input-text>
                <d2l-input-text id="employeeId" value="${user.employeeId}" label="Employee ID"></d2l-input-text>
                <d2l-input-text id="workPhone" value="${user.workPhone}" label="Work phone"></d2l-input-text>
                <d2l-input-text id="mobilePhone" value="${user.mobilePhone}" label="Mobile phone"></d2l-input-text>
                <d2l-input-text id="photoUrl" value="${user.photoUrl}" label="Photo URL"></d2l-input-text>
              </div>
            </d2l-expand-collapse-content>
            <d2l-button-subtle
              class="show-more-button"
              type="button"
              @click="${toggleExpandedUserAttributes}"
              text="Show more">
            </d2l-button-subtle>
            <d2l-button class="save-button" primary @click="${this.updateFussUser(user)}">Save</d2l-button>
            ${this._rolesTemplate(user)}
          </d2l-form>
        </d2l-list>
        `}
      </d2l-list-item>`;
  }

  _copyUrl(user) {
    return async e => {
      e.stopPropagation();
      const loginLink = await this._getLoginLink(user);
      navigator.clipboard.writeText(loginLink);
      this.client.createAlert('success', `Copied ${loginLink} to clipboard`);
    };
  }

  _stopProp(e) {
    e.stopPropagation();
  }

  _copyGuid(user) {
    return async e => {
      e.stopPropagation();
      const guid = user.guid;
      navigator.clipboard.writeText(guid);
      this.client.createAlert('success', `Copied ${guid} to clipboard`);
    };
  }

  _userClicked(user, newWindow = false) {
    return async e => {
      e.stopPropagation();
      const href = await this._getLoginLink(user);
      if (!href) return;

      if (newWindow) {
        window.open(href);
      } else {
        location.href = href;
      }

    };
  }

  async _getLoginLink(user) {
    const guid = user.guid;
    if (this.loginType === 'magic-link') {
      try {
        return await this.client.fetchLoginLink(this.environmentId, this.tenantId, guid, this.domain);
      } catch (err) {
        const message = await err.text();
        this.client.createAlert('error', `Invalid email address for tenant: ${message}`);
        return null;
      }
    }
    const isSponsor = this.tenant.entitlements
      .some(e => e.id === user.thirdPartyUserId && e.entitlement === 'sponsor');
    const relayState = isSponsor ? '/requests' : `/${this.relayState || ''}`;
    const host = this.client.isDev() ? 'http://localhost:3000' : `https://us-east-1.api.${location.hostname}`;
    return `${host}/api/sso/login/${this.environmentId}/${this.tenantId}?username=${guid}&relayState=${relayState}&host=${this.domain}`;
  }

  async _loadUserRoles(userGuid) {
    if (this._userRoles[userGuid] !== undefined) return;
    this._userRoles = { ...this._userRoles, [userGuid]: 'loading' };
    try {
      const roles = await this.client.fetchUserRoles(this.environmentId, userGuid);
      this._userRoles = { ...this._userRoles, [userGuid]: roles || [] };
    } catch {
      this._userRoles = { ...this._userRoles, [userGuid]: [] };
    }
  }

  _getRoleName(roleId) {
    const cleanId = roleId?.startsWith?.('role_') ? roleId.substring(5) : roleId;
    const role = this._tenantRoles.find(r => r.roleId === cleanId || r.roleId === roleId);
    return role?.roleName || cleanId || roleId;
  }

  _getRoleId(roleAssignment) {
    const id = roleAssignment?.roleId || roleAssignment;
    return id?.startsWith?.('role_') ? id.substring(5) : id;
  }

  _assignRoleToUser(userGuid) {
    return async() => {
      const select = this.shadowRoot.getElementById(`role-select-${userGuid}`);
      if (!select?.value) return;
      const roleId = select.value;
      try {
        await this.client.assignRole(this.environmentId, userGuid, roleId);
        this.client.createAlert('success', 'Role assigned successfully');
        this._userRoles = { ...this._userRoles, [userGuid]: undefined };
        await this._loadUserRoles(userGuid);
      } catch (err) {
        this.client.createAlert('error', `Failed to assign role: ${err.message || err}`);
      }
    };
  }

  _removeRoleFromUser(userGuid, roleId) {
    return async(e) => {
      e.stopPropagation();
      const cleanRoleId = this._getRoleId(roleId);
      try {
        await this.client.removeRole(this.environmentId, userGuid, cleanRoleId);
        this.client.createAlert('success', 'Role removed successfully');
        this._userRoles = { ...this._userRoles, [userGuid]: undefined };
        await this._loadUserRoles(userGuid);
      } catch (err) {
        this.client.createAlert('error', `Failed to remove role: ${err.message || err}`);
      }
    };
  }

  _rolesTemplate(user) {
    const roles = this._userRoles[user.guid];

    if (roles === undefined) {
      this._loadUserRoles(user.guid);
      return html`<div class="roles-section"><d2l-loading-spinner size="30"></d2l-loading-spinner></div>`;
    }

    if (roles === 'loading') {
      return html`<div class="roles-section"><d2l-loading-spinner size="30"></d2l-loading-spinner></div>`;
    }

    const assignedRoleIds = roles.map(r => this._getRoleId(r));
    const availableRoles = this._tenantRoles.filter(r => !assignedRoleIds.includes(r.roleId));

    return html`
      <div class="roles-section">
        <div class="roles-header">Roles</div>
        ${roles.length === 0 ? html`<div class="no-roles-warning">No roles assigned - user will not be able to log in if tenant requires roles</div>` : nothing}
        <div class="role-chips">
          ${roles.map(r => html`
            <span class="role-chip">
              ${this._getRoleName(r.roleId || r)}
              <d2l-button-icon text="Remove role" @click="${this._removeRoleFromUser(user.guid, r)}" icon="tier1:close-small"></d2l-button-icon>
            </span>
          `)}
        </div>
        ${availableRoles.length > 0 ? html`
          <div class="role-assign">
            <select id="role-select-${user.guid}" class="d2l-input-select" aria-label="Select a role">
              ${availableRoles.map(r => html`<option value="${r.roleId}">${r.roleName}</option>`)}
            </select>
            <d2l-button-subtle text="Assign role" icon="tier1:add" @click="${this._assignRoleToUser(user.guid)}"></d2l-button-subtle>
          </div>
        ` : nothing}
      </div>
    `;
  }

  _filterUsers(e) {
    this._filteredUsers = this.users.filter(user => {
      const compareValues = [getDisplayName(user), user.thirdPartyUserId, user.email, user.guid];
      return compareValues.some(value => value.toLowerCase().indexOf(e.target.value.toLowerCase()) >= 0);
    });
  }

  _addUser() {
    const newUser = {
      guid: uuid().toLowerCase(),
      tenantId: this.tenantId,
      firstName: 'New',
      lastName: 'User',
      expanded: true,
    };
    this.users = [newUser, ...this.users];
    this._filteredUsers = this.users;
  }

  async _openAddKrakenUserDialog() {
    if (this._krakenUsers.length === 0) {
      this._krakenUsers = await this.client.fetchUsers();
    }
    this._filteredKrakenUsers = [...this._krakenUsers];
    this.updateComplete.then(() => {
      this.shadowRoot.getElementById('add-kraken-user-dialog').opened = true;
    });
  }

  _filterKrakenUsers(e) {
    const query = e.target.value.toLowerCase();
    this._filteredKrakenUsers = this._krakenUsers.filter(user => {
      const values = [user.displayName, user.userId, user.email, user.firstName, user.lastName].filter(Boolean);
      return values.some(v => v.toLowerCase().includes(query));
    });
  }

  _handleSelectKrakenUser(krakenUser) {
    return () => this._selectKrakenUser(krakenUser);
  }

  async _selectKrakenUser(krakenUser) {
    const newUser = {
      guid: uuid().toLowerCase(),
      tenantId: this.tenantId,
      firstName: krakenUser.firstName || '',
      lastName: krakenUser.lastName || '',
      email: krakenUser.email || '',
      thirdPartyUserId: krakenUser.email || krakenUser.userId,
      active: true,
      expanded: true,
    };

    try {
      await this.client.pushToFuss(this.environmentId, this.tenantId, newUser);
      this.users = [newUser, ...this.users];
      this._filteredUsers = this.users;
      this.client.createAlert('success', `Added ${krakenUser.displayName || krakenUser.userId} as FUSS user`);
    } catch (err) {
      this.client.createAlert('error', `Failed to add user: ${err.message || err}`);
    }
  }

  _removeUserDialogTemplate() {
    return html`
      <d2l-dialog id="remove-user-dialog" title-text="Remove user">
        ${this._userToRemove
    ? html`<p>Are you sure you want to remove <span class="bold">${this._userToRemove.firstName} ${this._userToRemove.lastName}</span> with the guid
            <br/><span class="bold">${this._userToRemove.guid}</span>?</p>
          <p>
            It will delete the user in the following systems:
            <ul>
              <li>FUSS</li>
              <li>Wave</li>
              <li>Braze</li>
            </ul>
          </p>`
    : nothing}
        <d2l-button slot="footer" @click="${this._removeUser}" primary data-dialog-action="yes">Yes</d2l-button>
        <d2l-button slot="footer" data-dialog-action>No</d2l-button>
      </d2l-dialog>
    `;
  }

  _addKrakenUserDialogTemplate() {
    return html`
      <d2l-dialog id="add-kraken-user-dialog" title-text="Add Kraken User" width="600">
        <d2l-input-text
          @keyup="${this._filterKrakenUsers}"
          placeholder="Search Kraken users"
          label="Search Kraken users"
          label-hidden>
        </d2l-input-text>
        <d2l-list>
          ${this._filteredKrakenUsers.map(user => html`
            <d2l-list-item
              key="${user.userId}"
              label="${user.displayName || user.userId}"
              @click="${this._handleSelectKrakenUser(user)}"
              href="javascript:void(0)"
              data-dialog-action="done">
              <div>${user.displayName || `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.userId}</div>
              <div slot="supporting-info">${user.email || user.userId}</div>
            </d2l-list-item>
          `)}
        </d2l-list>
        <d2l-button slot="footer" data-dialog-action>Cancel</d2l-button>
      </d2l-dialog>
    `;
  }

  render() {
    return html`
      <d2l-input-text @keyup="${this._filterUsers}" autofocus placeholder="Search for users" label="Search for users" label-hidden></d2l-input-text>
      <d2l-list>
        ${this.readonly ? nothing : html`
        <d2l-list-controls slot="controls" slim>
          <d2l-button-subtle text="Add user" icon="tier1:add" @click="${this._addUser}"></d2l-button-subtle>
          <d2l-button-subtle text="Add Kraken user" icon="tier1:add" @click="${this._openAddKrakenUserDialog}"></d2l-button-subtle>
        </d2l-list-controls>
        `}
        ${this._filteredUsers.map(user => this._userListItemTemplate(user))}
      </d2l-list>
      ${this._removeUserDialogTemplate()}
      ${this._addKrakenUserDialogTemplate()}
    `;
  }

}

window.customElements.define('pm-fuss-users-list', FussUserList);
